/** VoicePro by XenonLabs.ai - Product Context Provider
 * Handles product-level configuration (VoicePro vs XeHirePro)
 * Products share the same codebase but have different branding and features
 */

import { createContext, useContext, ReactNode, useMemo } from "react";

export type ProductType = 'xevoicepro' | 'xehirepro';

export interface ProductConfig {
  id: ProductType;
  name: string;
  tagline: string;
  description: string;
  domain: string;
  primaryColor: string;
  secondaryColor: string;
  gradientFrom: string;
  gradientTo: string;
  logoText: string;
  features: string[];
  heroTitle: string;
  heroSubtitle: string;
  ctaText: string;
  defaultFeatures: {
    voiceAgents: boolean;
    recruitment: boolean;
    campaigns: boolean;
    flowBuilder: boolean;
  };
}

const PRODUCT_CONFIGS: Record<ProductType, ProductConfig> = {
  xevoicepro: {
    id: 'xevoicepro',
    name: 'VoicePro',
    tagline: 'AI Voice Agents for Business',
    description: 'Automate your calls with intelligent AI voice agents',
    domain: 'voicepro.xenonlabs.ai',
    primaryColor: '#6366f1',
    secondaryColor: '#8b5cf6',
    gradientFrom: '#6366f1',
    gradientTo: '#8b5cf6',
    logoText: 'VoicePro',
    features: [
      'AI Voice Agents',
      'Outbound Campaigns',
      'Inbound Call Handling',
      'CRM Integration',
      'Flow Builder',
      'Knowledge Base',
    ],
    heroTitle: 'AI Voice Agents That Close Deals',
    heroSubtitle: 'Automate outbound calls, qualify leads, and schedule appointments with human-like AI conversations',
    ctaText: 'Start Free Trial',
    defaultFeatures: {
      voiceAgents: true,
      recruitment: false,
      campaigns: true,
      flowBuilder: true,
    },
  },
  xehirepro: {
    id: 'xehirepro',
    name: 'XeHirePro',
    tagline: 'AI-Powered Recruitment Automation',
    description: 'Transform your hiring process with intelligent AI screening',
    domain: 'xehirepro.xenonlabs.ai',
    primaryColor: '#ec4899',
    secondaryColor: '#f472b6',
    gradientFrom: '#ec4899',
    gradientTo: '#8b5cf6',
    logoText: 'XeHirePro',
    features: [
      'AI Resume Screening',
      'Automated Candidate Calls',
      'Interview Scheduling',
      'Multi-Round Interviews',
      'Candidate Ranking',
      'Teams/Meet Integration',
    ],
    heroTitle: 'Hire Smarter, Faster with AI',
    heroSubtitle: 'From resume screening to final interviews - automate your entire recruitment pipeline',
    ctaText: 'Start Hiring',
    defaultFeatures: {
      voiceAgents: true,
      recruitment: true,
      campaigns: true,
      flowBuilder: true,
    },
  },
};

interface ProductContextState {
  product: ProductConfig;
  productId: ProductType;
  isXeHirePro: boolean;
  isXeVoicePro: boolean;
}

const ProductContext = createContext<ProductContextState | null>(null);

function detectProduct(): ProductType {
  if (typeof window === 'undefined') return 'xevoicepro';

  const hostname = window.location.hostname.toLowerCase();

  // Check for xehirepro domain patterns
  if (
    hostname.includes('xehirepro') ||
    hostname.includes('hirepro') ||
    // Development override
    new URLSearchParams(window.location.search).get('product') === 'xehirepro'
  ) {
    return 'xehirepro';
  }

  return 'xevoicepro';
}

export function ProductProvider({ children }: { children: ReactNode }) {
  const productId = useMemo(() => detectProduct(), []);
  const product = PRODUCT_CONFIGS[productId];

  const value = useMemo(() => ({
    product,
    productId,
    isXeHirePro: productId === 'xehirepro',
    isXeVoicePro: productId === 'xevoicepro',
  }), [product, productId]);

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProduct() {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
}

export function useProductConfig() {
  const { product } = useProduct();
  return product;
}

export { PRODUCT_CONFIGS };
